import { Button, Stack, Typography } from '@mui/material'
import { Controller } from 'react-hook-form'
import RemoveIcon from '@mui/icons-material/Remove'
import AddIcon from '@mui/icons-material/Add'
import { StyledSlider } from '../StyledSlider'
import { COLOR_PALLETE } from '~/COLOR_PALLETE'
import {
  DEFAULT_DYNAMIC_STEP_RULES,
  DynamicStepRule,
  getDynamicStep,
  roundToStep,
  sliderPositionToValue,
  valueToSliderPosition,
} from '../_utils/utils'

type Props = {
  suffix: string
  name: string
  control: any
  min: number
  max: number
  step: number
  disabled?: boolean
  hideMinMax?: boolean
  className?: string
  darkTheme?: boolean
  useDynamicSteps?: boolean
  dynamicStepRules?: DynamicStepRule[]
}

export const SliderWithButtons = ({
  suffix,
  name,
  control,
  min,
  max,
  step,
  disabled = false,
  hideMinMax,
  className = '',
  darkTheme = false,
  useDynamicSteps = false,
  dynamicStepRules = DEFAULT_DYNAMIC_STEP_RULES,
}: Props) => {
  return (
    <Controller
      name={name}
      control={control}
      render={({ field: { onChange, value } }) => {
        const dynamicStep = useDynamicSteps
          ? getDynamicStep(value, step, dynamicStepRules)
          : step
        const sliderValue = useDynamicSteps
          ? valueToSliderPosition(value, min, max)
          : value

        return (
          <Stack className={className}>
            <Stack className="flex-row justify-between items-center">
              <Button
                onClick={() => {
                  if (useDynamicSteps) {
                    const newValue = roundToStep(
                      value - dynamicStep,
                      dynamicStep,
                      min,
                      max
                    )
                    onChange(newValue)
                  } else {
                    const newValue = value - dynamicStep
                    if (newValue >= min) {
                      onChange(newValue)
                    }
                  }
                }}
                disabled={disabled || value <= min}
                className={`w-8 h-8 min-w-[unset] min-h-[unset] p-0 bg-zGrey400 text-zWhite200 ${
                  disabled ? 'cursor-not-allowed' : ''
                }`}>
                <RemoveIcon className="!w-5 !h-5" />
              </Button>

              <Stack className="items-center gap-[1px]">
                <Typography
                  className={`text-6.5d font-black text-${darkTheme ? COLOR_PALLETE.zBlack200 : COLOR_PALLETE.zWhite200} tracking-wide`}>
                  {suffix === 'rokov' && value !== 95 && value === max
                    ? 'doživotne'
                    : `${suffix === 'rokov' && value === 95 ? '+' : ''}${value.toLocaleString()} ${suffix}`}
                </Typography>
                {!hideMinMax && (
                  <Typography className={`text-xs font-semibold text-zGrey400`}>
                    {`Min ${min.toLocaleString('sk-SK')} ${suffix} / Max ${max.toLocaleString()} ${suffix}`}
                  </Typography>
                )}
              </Stack>

              <Button
                onClick={() => {
                  if (useDynamicSteps) {
                    const newValue = roundToStep(
                      value + dynamicStep,
                      dynamicStep,
                      min,
                      max
                    )
                    onChange(newValue)
                  } else {
                    const newValue = value + dynamicStep
                    if (newValue <= max) {
                      onChange(newValue)
                    }
                  }
                }}
                disabled={disabled || value >= max}
                className={`w-8 h-8 min-w-[unset] min-h-[unset] p-0 bg-zGrey400 text-zWhite200 ${
                  disabled ? 'cursor-not-allowed' : ''
                }`}>
                <AddIcon className="w-5 h-5" />
              </Button>
            </Stack>

            <Stack className="px-4">
              <StyledSlider
                darkTheme={darkTheme}
                min={useDynamicSteps ? 0 : min}
                max={useDynamicSteps ? 100 : max}
                step={useDynamicSteps ? 0.001 : step}
                value={sliderValue}
                onChange={(_, newSliderValue) => {
                  if (useDynamicSteps) {
                    const realValue = sliderPositionToValue(
                      newSliderValue as number,
                      min,
                      max
                    )
                    const currentStep = getDynamicStep(realValue, step)
                    const roundedValue = roundToStep(
                      realValue,
                      currentStep,
                      min,
                      max
                    )
                    onChange(roundedValue)
                  } else {
                    onChange(newSliderValue)
                  }
                }}
                disabled={disabled}
              />
            </Stack>
          </Stack>
        )
      }}
    />
  )
}
